/* eslint-disable @typescript-eslint/member-delimiter-style */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react'
import GetAppIcon from '@material-ui/icons/GetApp'

import moment, { Moment } from 'config/moment'
import { useApiCall } from 'hooks/useApi'

import { useLoadingTracker } from '../../hooks/useLoadingTracker'
import {
  formatCampaignFunnelMetric,
  campaignFunnelPct,
} from '../../utils/formatCampaignFunnelMetric'
import ComparisonTable from '../../components/ComparisonTable'
import {
  getManualCampaigns,
  ManualCampaignsParams,
  ManualCampaignsResponse,
  ManualCampaign,
} from './api'

interface ManualCampaignsProps {
  period?: { year: number; month: number }
  startPeriod1?: Moment
  endPeriod1?: Moment
  trigger?: number
  resetTrigger?: number
  onLoaded?: () => void
}

interface FormattedData {
  label: string
  [key: string]: string | number
}

const stripHtmlTags = (str: string | null | undefined) => {
  if (!str) return ''
  return str.replace(/<[^>]*>/g, '')
}

const formatCampaignRow = (campaign: ManualCampaign) => {
  const sent = Number(campaign.pushesSent) || 0
  const received = Number(campaign.pushesReceived) || 0
  const walkthrough = Number(campaign.walkthrough) || 0
  const redeemed = campaign.benefitsRedeemed

  return {
    label: '',
    _titleCampaign: stripHtmlTags(campaign.pushTitle || campaign.title || ''),
    _audienciaPotencial: formatCampaignFunnelMetric(sent, null, { isBaseline: true }),
    _audienciaImpactada: formatCampaignFunnelMetric(received, sent),
    _walkthrough: formatCampaignFunnelMetric(walkthrough, received),
    _beneficiosCanjeados: formatCampaignFunnelMetric(redeemed, walkthrough, { naWhenZero: true }),
  }
}

const ManualCampaigns: React.FC<ManualCampaignsProps> = ({
  period,
  startPeriod1,
  endPeriod1,
  trigger,
  resetTrigger,
  onLoaded,
}) => {
  const [data, setData] = useState<FormattedData[]>([])
  const [error, setError] = useState<string | null>(null)
  const [apiData, setApiData] = useState<ManualCampaign[] | null>(null)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  })

  const [manualCampaignsCall, statsLoading] = useApiCall<
    ManualCampaignsParams,
    ManualCampaignsResponse
  >(getManualCampaigns)

  const isLoading = statsLoading || isLoadingData

  useLoadingTracker({ isLoading, componentId: 'ManualCampaigns' })

  // Clear + loading only when Aplicar runs
  useEffect(() => {
    if (resetTrigger && resetTrigger > 0) {
      setApiData(null)
      setData([])
      setError(null)
      setIsLoadingData(true)
    }
  }, [resetTrigger])

  const fetchData = async () => {
    try {
      setIsLoadingData(true)
      setApiData(null)
      setData([])
      setError(null)

      // Use startPeriod1 and endPeriod1 if available, otherwise fallback to period calculation
      let startDate: string
      let endDate: string

      if (startPeriod1 && endPeriod1) {
        // Use the provided dates directly
        startDate = startPeriod1.format('YYYY-MM-DD')
        endDate = endPeriod1.format('YYYY-MM-DD')
      } else if (period) {
        // Fallback to period-based calculation
        // period.month is 0-11 (0 = January, 11 = December)
        const periodMoment = moment()
          .year(period.year)
          .month(period.month)
        startDate = periodMoment
          .clone()
          .startOf('month')
          .format('YYYY-MM-DD')
        endDate = periodMoment
          .clone()
          .endOf('month')
          .format('YYYY-MM-DD')
      } else {
        console.warn('Period is undefined, cannot fetch data')
        return
      }

      const response = await manualCampaignsCall({
        startDate,
        endDate,
      })

      if (response?.data) {
        setApiData(response.data)
      }
    } catch (apiError) {
      console.error('Error fetching data:', apiError)
      setError('Error al cargar datos de campañas manuales')
    } finally {
      setIsLoadingData(false)
      if (onLoaded) {
        onLoaded()
      }
    }
  }

  const formatDataForTable = () => {
    if (!apiData) {
      setData([])
      return
    }

    setData(apiData.map(formatCampaignRow))
    setError(null)
  }

  const setPaginationData = () => {
    if (apiData) {
      const totalPages = Math.ceil(apiData.length / rowsPerPage)
      setPagination({
        currentPage: 1,
        totalPages: totalPages || 1,
        hasNextPage: totalPages > 1,
        hasPrevPage: false,
      })
    }
  }

  const onPageChange = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }))
  }

  const onRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage)
    setPagination(prev => ({
      ...prev,
      currentPage: 1,
      totalPages: apiData ? Math.ceil(apiData.length / newRowsPerPage) || 1 : 1,
      hasNextPage: apiData ? Math.ceil(apiData.length / newRowsPerPage) > 1 : false,
      hasPrevPage: false,
    }))
  }

  const downloadManualCampaignsCSV = () => {
    if (!apiData?.length) return

    const rows: string[][] = []

    const headerRow = [
      'Campaña',
      'Audiencia potencial',
      'Audiencia potencial %',
      'Audiencia impactada',
      'Audiencia impactada %',
      'Walkthrough',
      'Walkthrough %',
      'Beneficios canjeados',
      'Beneficios canjeados %',
    ]
    rows.push(headerRow)

    apiData.forEach(row => {
      const sent = Number(row.pushesSent) || 0
      const received = Number(row.pushesReceived) || 0
      const walkthrough = Number(row.walkthrough) || 0
      const redeemed = row.benefitsRedeemed
      const redeemedNum = Number(redeemed)
      const redeemedIsNa = !Number.isFinite(redeemedNum) || redeemedNum === 0

      rows.push([
        stripHtmlTags(row.pushTitle || row.title || ''),
        sent.toString(),
        campaignFunnelPct(sent, null, { isBaseline: true }),
        received.toString(),
        campaignFunnelPct(received, sent),
        walkthrough.toString(),
        campaignFunnelPct(walkthrough, received),
        redeemedIsNa ? 'N/A' : redeemedNum.toString(),
        campaignFunnelPct(redeemed, walkthrough, { naWhenZero: true }),
      ])
    })

    const csvContent = rows.map(e => e.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'campanas_activas.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  useEffect(() => {
    if (trigger && trigger > 0 && period) {
      fetchData()
    }
  }, [trigger])

  useEffect(() => {
    formatDataForTable()
    setPaginationData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiData, rowsPerPage])

  return (
    <div className="space-y-4">
      <ComparisonTable
        title="Campañas activas del período"
        // header="Nombre de la campaña"
        stores={[
          {
            store: '',
            columns: [
              { label: 'Nombre de la campaña', key: 'titleCampaign' },
              { label: 'Audiencia potencial', key: 'audienciaPotencial' },
              { label: 'Audiencia impactada', key: 'audienciaImpactada' },
              { label: 'Walkthrough', key: 'walkthrough' },
              { label: 'Beneficios canjeados', key: 'beneficiosCanjeados' },
            ],
          },
        ]}
        data={data}
        downloadButton={
          <button
            type="button"
            className="p-2 rounded border border-gray-300 hover:bg-gray-100 transition"
            aria-label="Descargar CSV"
            onClick={downloadManualCampaignsCSV}
          >
            <GetAppIcon />
          </button>
        }
        isLoading={isLoading}
        error={error}
        emptyMessage="No hay datos disponibles para mostrar en este momento."
        {...pagination}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </div>
  )
}

ManualCampaigns.defaultProps = {
  period: undefined,
  startPeriod1: undefined,
  endPeriod1: undefined,
  trigger: 0,
  resetTrigger: 0,
  onLoaded: undefined,
}

export default ManualCampaigns
